import React, { useEffect, useState } from 'react';
import { useMount } from '@shared/utils/hooks/useMount';
import PageNotFound from '@apps/pageNotFound/PageNotFound';
import { useHotelBookingGuestSiteTelemetry } from '../../HotelBooking.telemetry';
import { useEventInfo } from '@shared/utils/eventInfo';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import { useTravelPlacesPricingAndBookingDetailsQuery } from '@shared/hooks/useTravelPlacesPricingAndBookingDetails';
import { PartnerHotelRedirect } from '../../components/PartnerHotelRedirect';
import { withWindow } from '@shared/utils/withWindow';

/**
 * Handles the accommodation place partner redirect
 *
 * @param {Object} props - The props object for the component.
 * @param {string} props.accommodationPlaceV2ByIdId - The ID of the accommodation place.
 * @returns {JSX.Element} The component's JSX. - Redirect to partner hotel or hotel or 404.
 */

export const AccommodationPlacePartner = ({ accommodationPlaceV2ByIdId }: { accommodationPlaceV2ByIdId: string }) => {
  const [showNotFound, setShowNotFound] = useState<boolean>(false);
  const { eventInfo, loading: eventInfoLoading } = useEventInfo();

  const telemetry = useHotelBookingGuestSiteTelemetry();

  const { data, loading: loadingData, error: travelPlacesPricingAndBookingDetailsQueryError } = useTravelPlacesPricingAndBookingDetailsQuery(
    [accommodationPlaceV2ByIdId],
    'accommodationPlace',
    eventInfo?.finalizedDate?.milliseconds
  );

  const isLoading = loadingData || eventInfoLoading;

  const accommodationsPlaceV2Data = data?.travelPlacesPricingAndBookingDetails?.accommodationPlaces?.[0];
  const accommodationPlace = accommodationsPlaceV2Data?.accommodationPlace;
  const redirectMechanisms = accommodationsPlaceV2Data?.redirectMechanisms;
  const pricingInfo = accommodationsPlaceV2Data?.pricingInfo;
  const googleMaps = {
    lat: accommodationPlace?.lat,
    lng: accommodationPlace?.long,
    gplid: accommodationPlace?.googlePlaceId,
    name: accommodationPlace?.name,
    address: accommodationPlace?.address,
    zentrumHubId: accommodationPlace?.zhId
  };

  useEffect(() => {
    if (isLoading) return;
    if (travelPlacesPricingAndBookingDetailsQueryError) {
      telemetry.guestPartnerHotelBookingRedirect({
        destinationUrl: new URL(withWindow(global => global.location.href, '')),
        destinationName: 'notFound'
      });
      setShowNotFound(true);
    }
  }, [isLoading, telemetry, travelPlacesPricingAndBookingDetailsQueryError]);

  useMount(() => {
    telemetry.accommodationsPlace.enter();
  });

  return showNotFound ? (
    <PageNotFound />
  ) : (
    <>
      {isLoading ? (
        <JoyLogoLoader loaderKey="guest-hotel-booking-accommodations-place-v3" />
      ) : (
        <PartnerHotelRedirect
          telemetryName="guestPartnerHotelBookingRedirect"
          travelPlace={{
            id: accommodationPlaceV2ByIdId,
            name: accommodationPlace?.name,
            customUrl: accommodationPlace?.customUrl,
            url: accommodationPlace?.url,
            type: 'accommodationPlace'
          }}
          redirectMechanisms={redirectMechanisms}
          googleMaps={googleMaps}
          pricingInfo={pricingInfo}
          eventData={{ eventId: eventInfo?.eventId, eventDate: eventInfo?.finalizedDate?.dateString }}
          pageNotFoundCallback={() => setShowNotFound(true)}
        />
      )}
    </>
  );
};
